"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, Check, Clock, DollarSign, FileText, Hammer, Layers, Paintbrush, ShieldCheck, Sparkles, ArrowRight, TrendingUp, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface CostTier {
  id: string;
  badge: string;
  title: string;
  priceRange: string;
  timeline: string;
  description: string;
  featured?: boolean;
  scopeItems: string[];
  ctaText: string;
}
const costTiers: CostTier[] = [{
  id: "refresh",
  badge: "Essential Transformation",
  title: "Kitchen Refresh & Layout Preservation",
  priceRange: "$45,000 – $75,000",
  timeline: "6–8 Weeks",
  description: "Designed for structurally sound Decatur floor plans where the existing work triangle functions well. We replace tired surfaces with durable engineered quartz, semi-custom shaker or flat-panel hardwood cabinetry with soft-close Blum hardware, designer tile backsplashes, and premium mid-tier appliance suites (such as Bosch or KitchenAid 300/800 series) without altering structural walls or core plumbing stacks.",
  featured: false,
  scopeItems: ["Semi-custom maple or oak cabinetry with dove-tail soft-close drawers and pantries", "Fabricated 3cm quartz or honed granite countertops with eased edge profiles", "Handcrafted subway, zellige, or artisan ceramic tile backsplashes with waterproof backing", "Plumbing fixture replacement in place (undermount sinks, pull-down brass/nickel faucets)", "Targeted electrical updates: dedicated 20A small appliance circuits and under-cabinet LED arrays", "Hardwood floor patching, sanding, and dustless refinishing with durable polyurethane", "Trade permitting and required municipal mechanical inspections"],
  ctaText: "Discuss Refresh Scope"
}, {
  id: "custom",
  badge: "Most Selected",
  title: "Complete Custom Reconfiguration",
  priceRange: "$80,000 – $140,000",
  timeline: "10–14 Weeks",
  description: "Our signature renovation for Decatur homeowners seeking an optimized layout, open kitchen-to-dining flow, and high-performance culinary workstations. We remove non-load-bearing partition walls, relocate gas/water rough-ins to center prep islands with counter seating, install full-height bespoke cabinetry with integrated trash pullouts and spice racks, and integrate luxury appliance packages (Thermador, Wolf/Sub-Zero, or Café series) along with upgraded 200A electrical service panels.",
  featured: true,
  scopeItems: ["Fully custom architectural cabinetry extending to ceiling with crown and integrated panels", "Premium Taj Mahal quartzite, Calacatta quartz, or honed marble slabs with waterfall edges", "Full utility relocation: moving sink, gas range, and refrigerator rough-ins for optimal workflow", "Expansive 8-to-10 foot central island with integrated prep sink, microwave drawer, and seating", "Main electrical service upgrade (100A to 200A panel) to support modern induction and wall ovens", "Architectural recessed task lighting, dimmable ambient zones, and dedicated 600+ CFM exterior venting", "Decatur zoning compliance, engineering stamps, and complete historic neighborhood sign-offs"],
  ctaText: "Plan Custom Kitchen"
}, {
  id: "historic",
  badge: "Architectural Scope",
  title: "Historic Reconfiguration & Footprint Expansion",
  priceRange: "$150,000+",
  timeline: "16–20 Weeks",
  description: "Comprehensive structural transformations designed for historic Craftsman bungalows in Oakhurst, Winnona Park, and mid-century ranches in Clairemont/Great Lakes. We remove load-bearing masonry walls using recessed steel or LVL beams to unify compartmentalized rooms, correct sagging floor joists and out-of-square vintage framing, replace deteriorated galvanized piping with PEX-A, incorporate architectural bump-outs with energy-efficient Marvin/Pella windows, and install commercial-grade 48-inch dual-fuel ranges.",
  featured: false,
  scopeItems: ["Load-bearing wall removal with engineer-stamped flush-mount steel I-beams or LVL headers", "Subfloor structural stabilization, crawlspace sistering, and precision laser floor leveling", "Complete replacement of vintage galvanized plumbing and outdated ungrounded cloth wiring", "Seamless rear kitchen bump-outs or porch integrations matching exterior Decatur siding and brickwork", "Custom architectural millwork, walk-in scullery/butler's pantries, and integrated paneled appliances", "High-output 1,000+ CFM commercial ventilation with conditioned make-up air damper systems", "Full Decatur Historic Preservation Commission (HPC) coordination and Certificate of Appropriateness (COA)"],
  ctaText: "Explore Architectural Scope"
}];
const costVsValueData = [{
  project: "Minor kitchen remodel (midrange)",
  cost: "$28,359",
  resaleValue: "$31,712",
  recouped: "111.8%"
}, {
  project: "Major kitchen remodel (midrange)",
  cost: "$79,591",
  resaleValue: "$41,805",
  recouped: "52.5%"
}];
interface TimelinePhase {
  number: string;
  title: string;
  duration: string;
  description: string;
  details: string;
  iconName: "plan" | "demo" | "cabinet" | "finish" | "inspection";
}
const timelinePhases: TimelinePhase[] = [{
  number: "01",
  title: "Planning, Design & Permitting",
  duration: "Weeks 1–4 (Pre-construction)",
  description: "Finalizing 3D architectural schematics, selecting cabinet finishes and stone slabs, ordering long-lead appliances, and submitting complete drawing sets for City of Decatur or DeKalb County plan review.",
  details: "All bespoke cabinetry and specialty plumbing fixtures are secured prior to physical site work to eliminate idle downtime on-site.",
  iconName: "plan"
}, {
  number: "02",
  title: "Demolition & Structural Corrections",
  duration: "Weeks 1–3 of On-Site Work",
  description: "Dust-contained demolition under negative air scrubbers. Removing vintage partitions, installing structural LVL beams, sistering unlevel ceiling/floor joists, and furring out out-of-plumb historic plaster walls.",
  details: "Older Decatur bungalows and ranches receive subfloor reinforcement and framing true-ups before utility installation begins.",
  iconName: "demo"
}, {
  number: "03",
  title: "Rough Mechanicals & Municipal Inspections",
  duration: "Weeks 4–6",
  description: "Routing dedicated 200A electrical circuits, high-performance exhaust ducting, and PEX-A water distribution. Passing rigorous City of Decatur rough framing, plumbing, electrical, and mechanical trade inspections.",
  details: "Includes thorough leak testing and insulation inspection before closed-wall approvals are signed by municipal inspectors.",
  iconName: "inspection"
}, {
  number: "04",
  title: "Drywall, Cabinetry & Stone Templating",
  duration: "Weeks 7–10",
  description: "Level-5 drywall finishing, hardwood floor integration, laser-level cabinet hanging with custom scribe molding, and precision digital templating for 3cm quartz or natural stone slab fabrication.",
  details: "Cabinet cases are mechanically anchored and leveled to historical walls, accommodating slight vintage architectural shifts.",
  iconName: "cabinet"
}, {
  number: "05",
  title: "Surfaces, Fixtures & Final Sign-Off",
  duration: "Weeks 11–14",
  description: "Countertop placement, artisan tile backsplash installation, decorative pendant hanging, appliance calibration, make-up air testing, final punch list execution, and issuing the official Decatur Certificate of Completion.",
  details: "A comprehensive room-by-room walk-through guarantees all soft-close hinges, drawer runners, and caulking beads meet our master standards.",
  iconName: "finish"
}];
const faqs = [{
  question: "How do City of Decatur permitting timelines compare to unincorporated DeKalb County?",
  answer: "The City of Decatur operates its own dedicated building department with an independent plan review staff located at 2635 Talley Street. Decatur residential kitchen permits with structural modifications or trade alterations generally require 2 to 4 weeks for initial plan review, with expedited trade permits often taking 7 to 10 business days. If your property is in a designated local historic district (such as McDonough-Adams-Kings Highway or Clairemont), external modifications require a Certificate of Appropriateness (COA) from the Historic Preservation Commission (HPC), which meets monthly and adds 3 to 4 weeks to pre-construction planning. In contrast, properties in unincorporated DeKalb County submit through DeKalb County Planning & Sustainability, where residential review cycles typically range from 4 to 8 weeks depending on department backlogs. We manage 100% of municipal submittals, structural engineer stamps, and inspection schedules for both jurisdictions."
}, {
  question: "What unique structural and utility challenges are common in older Decatur bungalows and ranches?",
  answer: "Decatur's housing stock features distinct architectural eras with specific hidden conditions: 1920s–1940s Craftsman bungalows (frequently found in Oakhurst, Winnona Park, and Lenox Park) often have original 60A or 100A electrical service panels inadequate for modern induction cooktops, dual ovens, and microwave drawers; ungrounded knob-and-tube or cloth-insulated wiring; aging galvanized water pipes that restrict water pressure; and unlevel 2x8 floor joists that have settled over a century. 1950s–1960s mid-century brick ranches (common in Clairemont, Great Lakes, and Medlock Park) frequently feature low 8-foot ceiling heights, masonry interior load-bearing partition walls, cast-iron drain lines subject to corrosion, and out-of-square framing. During our pre-construction exploratory phase, we use thermal imaging, digital laser levels, and crawlspace inspections to identify these elements early, engineering panel upgrades to 200A, subfloor sistering, and PEX-A re-piping into the firm scope."
}, {
  question: "What specific factors extend or shorten a Decatur kitchen remodel timeline?",
  answer: "The four primary factors influencing schedule duration are: (1) Cabinetry Lead Times: Semi-custom factory cabinets arrive in 4 to 6 weeks, whereas bespoke architectural cabinetry with custom paint matches and integrated appliance paneling requires 8 to 12 weeks of shop fabrication; (2) Structural Scope: Preserving existing exterior walls and footprint shortens on-site work to 6–8 weeks, while removing load-bearing walls, installing recessed steel beams, or constructing rear bump-outs adds 4 to 6 weeks for engineering, shoring, and framing; (3) Appliance Availability: Specialty luxury appliances (such as 48-inch dual-fuel ranges or integrated column refrigeration) can carry 12-to-24 week manufacturer backorders, which is why we order all appliances upon design sign-off during pre-construction; and (4) Municipal Historic Review: Exterior window changes, roofline alterations, or structural footprint additions in designated historic overlays require monthly HPC board hearings before permit issuance."
}, {
  question: "What contingency budget should I allocate for an older Decatur home?",
  answer: "For historic Craftsman homes built prior to 1950, we advise allocating a realistic 8% to 12% contingency reserve to address hidden conditions discovered once walls and subfloors are opened (such as outdated junction boxes behind plaster, deteriorated subflooring beneath old linoleum layers, or undersized historic joist spans). For 1960s and newer homes, a 5% to 8% contingency is generally sufficient. Because we conduct thorough pre-construction structural inspections and laser framing checks, the vast majority of our projects complete within 2% to 3% of the agreed contract amount."
}, {
  question: "How are change orders managed if I adjust materials mid-project?",
  answer: "Every revision—whether upgrading to a waterfall quartzite edge, adding under-cabinet strip receptacles, or switching tile vendors—is documented in a transparent written Change Order before execution. The document clearly itemizes material cost variances, associated labor adjustments, and precise schedule impacts. Physical work on the revised scope only proceeds once you have reviewed and electronically authorized the change, eliminating unexpected budget surprises at project wrap-up."
}, {
  question: "Can our family remain living in the home during a major kitchen remodel?",
  answer: "Yes, over 80% of our Decatur clients choose to remain in their homes during the renovation. To keep your household comfortable and healthy, we establish ZipWall magnetic dust barrier containment with negative-air HEPA scrubbers, install heavy-duty Ram Board floor protection across all transit hallways, and set up a fully operational temporary kitchenette equipped with your refrigerator, countertop convection oven, microwave, coffee station, and dedicated dining space."
}];
export default function CostTimeline() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const getPhaseIcon = (name: TimelinePhase["iconName"]) => {
    switch (name) {
      case "plan":
        return <FileText className="size-5 text-chart-1" />;
      case "demo":
        return <Hammer className="size-5 text-chart-1" />;
      case "cabinet":
        return <Layers className="size-5 text-chart-1" />;
      case "finish":
        return <Paintbrush className="size-5 text-chart-1" />;
      case "inspection":
        return <ShieldCheck className="size-5 text-chart-1" />;
      default:
        return <Sparkles className="size-5 text-chart-1" />;
    }
  };
  return <section id="cost-timeline" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors duration-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-0.5 w-6 bg-chart-1" />
            <span className="text-xs sm:text-sm font-bold tracking-widest uppercase text-muted-foreground font-sans">
              Investment &amp; Schedule
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-foreground tracking-tight leading-[1.2] mb-4">
            Clear Cost &amp; Timeline Expectations for Decatur Kitchens
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Planning a kitchen remodel in Decatur requires balancing architectural character with modern culinary performance. Whether you own a 1920s Craftsman bungalow in Oakhurst with compartmentalized framing and vintage plumbing or a 1950s brick ranch in Clairemont needing load-bearing wall removal, our transparent investment tiers, rigorous municipal permitting workflows, and phased schedules ensure your renovation completes on budget and on time.
          </p>
        </motion.div>

        {/* Cost Tier Breakdown (3-Column Grid) */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12 lg:mb-16 items-stretch">
          {costTiers.map((tier, index) => <motion.div key={tier.id} data-index={index} variants={itemVariants} className="flex">
              <Card className={`relative flex flex-col justify-between w-full border bg-card text-card-foreground rounded-xl transition-[border-color,box-shadow,transform] duration-300 hover:shadow-md hover:-translate-y-1 ${tier.featured ? "border-chart-1/60 shadow-sm ring-1 ring-chart-1/30" : "border-border shadow-2xs"}`}>
                {/* Architectural Copper Top Hairline on Featured Card */}
                {tier.featured && <div className="absolute top-0 left-0 right-0 h-1 bg-chart-1 rounded-t-xl" />}

                <div>
                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <Badge variant={tier.featured ? "default" : "secondary"} className={tier.featured ? "bg-chart-1 text-white border-transparent font-medium" : "font-medium"}>
                        {tier.badge}
                      </Badge>
                      <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium bg-muted px-2.5 py-1 rounded-full">
                        <Clock className="size-3 text-chart-1" />
                        <span>{tier.timeline}</span>
                      </div>
                    </div>

                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground leading-snug">
                      {tier.title}
                    </CardTitle>

                    <div className="mt-3 pt-3 border-t border-border/60">
                      <span className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-sans">
                        {tier.priceRange}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5 font-sans">
                        Typical Decatur turnkey investment
                      </span>
                    </div>

                    <CardDescription className="text-sm text-muted-foreground mt-3 font-sans leading-relaxed">
                      {tier.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <div className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3 font-sans">
                      Included Scope Elements:
                    </div>
                    <ul className="space-y-2.5 text-sm text-foreground/90 font-sans">
                      {tier.scopeItems.map((item, itemIdx) => <li key={itemIdx} className="flex items-start gap-2.5">
                          <Check className="size-4 shrink-0 text-chart-1 mt-0.5" />
                          <span className="text-xs sm:text-sm text-muted-foreground leading-snug">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-6 border-t border-border/40">
                  <Button asChild variant={tier.featured ? "default" : "secondary"} className={`w-full font-sans font-semibold transition-[background-color,border-color,color,box-shadow] ${tier.featured ? "bg-chart-1 text-white hover:bg-chart-1/90 shadow-sm" : "border border-border bg-secondary hover:bg-muted text-foreground"}`}>
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <span>{tier.ctaText}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Atlanta Remodeling Cost vs Value (2025) Table */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-16 lg:mb-24">
          <div className="bg-card/90 backdrop-blur-md border border-border rounded-xl p-6 sm:p-8 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-border/70">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-chart-1/10 text-chart-1">
                  <TrendingUp className="size-5" />
                </div>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                    Atlanta remodeling cost vs value (2025)
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans mt-0.5">
                    Benchmark returns for residential kitchen investments across the greater Atlanta metropolitan market.
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="border-border text-muted-foreground text-xs self-start sm:self-auto font-sans">
                Metro Atlanta Data
              </Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse font-sans">
                <caption className="text-left text-xs text-muted-foreground italic mb-3 font-sans">
                  National survey averages for the Atlanta market, not quotes
                </caption>
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-secondary/50">
                    <th scope="col" className="py-3.5 px-4 font-semibold rounded-l-md">Project</th>
                    <th scope="col" className="py-3.5 px-4 font-semibold">Average Cost</th>
                    <th scope="col" className="py-3.5 px-4 font-semibold">Resale Value Added</th>
                    <th scope="col" className="py-3.5 px-4 font-semibold text-right rounded-r-md">% Cost Recouped</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60 text-sm">
                  {costVsValueData.map((row, idx) => <tr key={idx} data-index={idx} className="hover:bg-secondary/30 transition-colors">
                      <td className="py-4 px-4 font-medium text-foreground">
                        {row.project}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground font-mono font-medium">
                        {row.cost}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground font-mono font-medium">
                        {row.resaleValue}
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-chart-1 font-mono">
                        {row.recouped}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-muted-foreground font-sans">
                Source: Remodeling Magazine / JLC Cost vs Value Report 2025, Atlanta, GA metro.
              </p>
              <p className="text-[11px] text-muted-foreground font-sans italic">
                Actual ROI varies based on neighborhood comps, historical detailing, and material selections.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Process Timeline & Milestones */}
        <div className="mb-16 lg:mb-24 pt-8 border-t border-border">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 mb-2">
              <SafeHydrate><Calendar className="size-4 text-chart-1" /></SafeHydrate>
              <span className="text-xs sm:text-sm font-bold tracking-widest uppercase text-muted-foreground font-sans">
                Milestone Roadmap
              </span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground tracking-tight mb-2">
              Sequential Steps From Demolition to Completion
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
              Our structured 5-phase framework synchronizes architectural design, long-lead procurement, structural framing true-ups, and municipal inspections so your Decatur home undergoes a smooth, predictable transformation.
            </p>
          </motion.div>

          {/* Structured Milestone Grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 lg:gap-6">
            {timelinePhases.map((phase, idx) => <motion.div key={phase.number} data-index={idx} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : idx * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="bg-card border border-border rounded-lg p-5 flex flex-col justify-between transition-colors hover:border-chart-1/40 shadow-2xs">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="font-mono text-xs font-bold text-chart-1 tracking-wider bg-chart-1/10 px-2 py-0.5 rounded">
                      PHASE {phase.number}
                    </span>
                    <div className="p-1.5 rounded-md bg-secondary/80">
                      {getPhaseIcon(phase.iconName)}
                    </div>
                  </div>

                  <h4 className="font-serif font-semibold text-base text-foreground mb-1">
                    {phase.title}
                  </h4>

                  <span className="inline-block text-xs font-medium text-chart-1/90 mb-3 font-sans">
                    {phase.duration}
                  </span>

                  <p className="text-xs text-muted-foreground font-sans leading-relaxed mb-3">
                    {phase.description}
                  </p>
                </div>

                <div className="pt-3 border-t border-border/50 text-[11px] text-muted-foreground italic font-sans">
                  {phase.details}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Interactive Accordion FAQ Block */}
        <div className="pt-8 border-t border-border max-w-4xl mx-auto">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground tracking-tight mb-3">
              Budget, Permitting &amp; Scheduling Insights
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-sans max-w-2xl mx-auto">
              Clear answers regarding City of Decatur vs. DeKalb County permitting timelines, older bungalow infrastructure, and practical trade management.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="bg-card border border-border rounded-lg px-4 sm:px-6 shadow-2xs transition-colors hover:border-chart-1/30">
                  <AccordionTrigger className="text-left font-serif font-semibold text-sm sm:text-base text-foreground hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>

          {/* Consultation Note Callout */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-10 p-6 rounded-xl bg-secondary/60 border border-border text-center flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-left">
              <h4 className="font-serif font-semibold text-base text-foreground">
                Need a tailored line-item cost proposal for your Decatur home?
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground font-sans mt-0.5">
                We perform an on-site structural walk-through and deliver a fixed-scope estimate with guaranteed scheduling.
              </p>
            </div>
            <Button asChild className="bg-chart-1 text-white hover:bg-chart-1/90 shrink-0 font-sans font-semibold">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>;
}